<template>
  <div>
    <b-overlay :show="isLoading" rounded="sm">
      <b-navbar toggleable="lg" type="dark" variant="light">
        <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
        <b-navbar-brand href="#" class="text-dark">
          <strong>Benchmark Third Parties</strong>
        </b-navbar-brand>

        <b-collapse id="nav-collapse" is-nav>
          <!-- Right aligned nav items -->
          <b-navbar-nav class="ml-auto">
            <b-nav-form>
              <b-button
                class="my-2 my-sm-0 mr-2"
                variant="outline-primary"
                size="sm"
                @click="showAddThirdPartyModal = !showAddThirdPartyModal"
              >
                <font-awesome-icon icon="plus" />
              </b-button>
              <b-button
                class="my-2 my-sm-0 mr-2"
                variant="outline-primary"
                size="sm"
                @click="showBulkUploadModal"
              >
                <font-awesome-icon icon="file-excel" />
              </b-button>
              <b-button variant="outline-info" size="sm" class="mr-4" @click="reloadTable">
                <font-awesome-icon icon="sync" />
              </b-button>
              <b-form-group>
                <b-input-group size="sm">
                  <b-form-input
                    v-model="filter"
                    type="search"
                    id="filterInput"
                    placeholder="Type to Search"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-nav-form>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>
      <b-table
        bordered
        sticky-header="calc(100vh - 14rem)"
        ref="table"
        id="my-table"
        fixed
        :fields="staticFields"
        :busy.sync="isLoading"
        :items="getBenchMarkThirdPartiesData"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        responsive="sm"
        :per-page="perPage"
        :current-page="currentPage"
        :filter="filter"
      >
        <template v-slot:cell(comp_name_eng)="data">
          <p class="text-wrap">{{data.item.comp_name_eng}}</p>
        </template>
        <template v-slot:cell(tpi_type)="data">
          <font-awesome-icon :icon="data.item.tpi_type== 'O' ? 'building' : 'male'" class="mr-2" />
        </template>
        <template v-slot:cell(actions)="data">
          <b-button
            size="sm"
            class="mr-2"
            type="button"
            variant="outline-info"
            @click="showEditModal(data)"
          >
            <font-awesome-icon v-b-tooltip.hover title="Edit Field" icon="edit" />
          </b-button>
          <b-button
            size="sm"
            type="button"
            variant="outline-warning"
            @click="openThirdpartyProfile(data)"
          >
            <font-awesome-icon icon="users" />
          </b-button>
        </template>
      </b-table>
      <p class="text-center mt-3">
        Showing Result From
        <b>{{fromRows}}</b> to
        <b>{{toRows}}</b>
      </p>
      <b-row>
        <b-col cols="2">
          <b-form-select v-model="perPage" id="perPageSelect" size="sm" :options="pageOptions"></b-form-select>
        </b-col>
        <b-col>
          <b-pagination
            class="float-right"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            first-text="First"
            prev-text="Prev"
            next-text="Next"
            last-text="Last"
            aria-controls="my-table"
          ></b-pagination>
        </b-col>
      </b-row>
    </b-overlay>
    <b-modal
      scrollable
      no-close-on-backdrop
      v-model="showAddThirdPartyModal"
      size="xl"
      :title="editThirdParty ? 'Update Third Party' : 'Create New Third Party'"
    >
      <!-- form start --------------------------- -->
      <b-form v-if="!submiting && !responseSuccess">
        <b-row>
          <b-col cols="6">
            <b-form-group>
              <label>
                Third Party Name:
                <span class="text-danger">*</span>
              </label>
              <b-form-input
                size="sm"
                v-model="$v.form.thirdPartyName.$model"
                :state="$v.form.thirdPartyName.$dirty ? !$v.form.thirdPartyName.$error : null"
                required
                placeholder="Enter Third Party Name"
              ></b-form-input>
            </b-form-group>
            <b-form-group>
              <label>
                Entity Type
                <span class="text-danger">*</span>
              </label>
              <b-form-select
                v-model="$v.form.selectedEntityType.$model"
                :state="$v.form.selectedEntityType.$dirty ? !$v.form.selectedEntityType.$error : null"
                :options="entityTypes"
              ></b-form-select>
            </b-form-group>
            <b-form-group>
              <label>
                {{ form.selectedEntityType == 'O' ? 'Country of Registration' : ' Country of Residence'}}
                <span
                  class="text-danger"
                >*</span>
              </label>
              <v-select
                label="name"
                v-model="$v.form.selectedCountry.$model"
                :state="$v.form.selectedCountry.$dirty ? !$v.form.selectedCountry.$error : null"
                :class="$v.form.selectedCountry.$error ? 'border border-danger rounded' : null"
                :options="optionsCountries"
                @input="setCountry"
              >
                <template slot="option" slot-scope="option">
                  <country-flag v-if="option.icon" :country="option.icon" size="small" />
                  <span>{{ '&nbsp;'+ option.name }}</span>
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group>
              <label>Website:</label>
              <b-form-input size="sm" v-model="form.website" placeholder="Enter Website"></b-form-input>
            </b-form-group>
            <b-form-group>
              <label>Registration Number:</label>
              <b-form-input
                size="sm"
                v-model="form.registrationNumber"
                placeholder="Enter Registration Number"
              ></b-form-input>
            </b-form-group>
            <b-form-group>
              <label>
                Pri Industries:
                <!-- <span class="text-danger">*</span> -->
              </label>
              <v-select
                class="mb-2"
                label="text"
                v-model="form.selectedPriIndustries"
                :options="optionsPriIndustries"
                @input="setPriIndustriesValue"
              ></v-select>
              <!-- :state="$v.form.selectedPriIndustries.$dirty ? !$v.form.selectedPriIndustries.$error : null"
              :class="$v.form.selectedPriIndustries.$error ? 'border border-danger rounded' : null"-->
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <div v-else-if="submiting && !responseSuccess">
        <p>{{editThirdParty ? 'Your Third Party is being Updating......' : 'Your Third Party is being Creating......'}}</p>
        <b-spinner type="grow" label="Loading..."></b-spinner>
      </div>
      <div v-else>
        <p class="my-2 text-center">
          {{ editThirdParty ? 'Your Third Party has been updated successfully.' : 'Your Third Party has been Created successfully.' }}
          <br />
        </p>
      </div>
      <template v-slot:modal-footer>
        <div class="float-right">
          <b-button
            v-if="!submiting"
            class="mr-3"
            variant="outline-danger"
            @click="resetModal"
          >Close</b-button>
          <b-button
            v-if="!submiting && !responseSuccess"
            variant="outline-success"
            @click="checkValidation"
          >{{ editThirdParty? 'Update' : 'Create' }}</b-button>
        </div>
      </template>
    </b-modal>
    <BenchmarkBulkUploadModal :fileFormats="fileFormats" />
  </div>
</template>
<script>
import { HTTP } from "../../utils/HTTP";
import { EventBus } from "@/event-bus.js";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import countriesData from "../../utils/countryCodes.json";
import BenchmarkBulkUploadModal from "../../components/bulkUpload/benchmarkBulkUploadModal.vue";
export default {
  name: "PriSettingsBenchmarkThirdParties",
  mixins: [validationMixin],
  components: {
    BenchmarkBulkUploadModal
  },
  data() {
    return {
      totalRows: 0,
      isLoading: false,
      filter: null,
      perPage: 10,
      currentPage: 1,
      sortBy: "id",
      sortDesc: true,
      staticFields: [
        { key: "comp_name_eng", label: "Third Party Name", sortable: true },
        { key: "tpi_type", label: "Entity Type", sortable: false },
        { key: "country", label: "Country", sortable: true },
        { key: "website", label: "Website", sortable: true },
        {
          key: "registration_number",
          label: "Registration Number",
          sortable: true
        },
        { key: "industry", label: "Industry", sortable: true },
        { key: "actions", label: "Actions", sortable: false }
      ],
      pageOptions: [5, 10, 25, 50, 75, 100],
      fromRows: 0,
      toRows: 0,
      editThirdParty: false,
      optionsCountries: countriesData.countries,
      entityTypes: [
        { text: "Organisation", value: "O" },
        { text: "Individual", value: "P" }
      ],
      showAddThirdPartyModal: false,
      form: {
        thirdPartyName: null,
        selectedEntityType: null,
        selectedCountry: null,
        website: null,
        registrationNumber: null,
        selectedPriIndustries: null
      },
      optionsPriIndustries: [],
      submiting: false,
      responseSuccess: false,
      thirdPartyId: null,
      countryValue: null,
      fileFormats: [{ text: "Excel 2012", value: "application/vnd.ms-excel" }],
      priIndustriesValue: null
    };
  },
  validations: {
    form: {
      thirdPartyName: {
        required
      },
      selectedEntityType: {
        required
      },
      selectedCountry: {
        required
      }
      // selectedPriIndustries: {
      //   required
      // }
    }
  },
  computed: {
    rows() {
      return this.totalRows;
    }
  },
  mounted() {
    this.getPriIndustriesOptions();
  },
  methods: {
    showBulkUploadModal() {
      EventBus.$emit("onShowBulkUploadModal", "");
    },
    reloadTable() {
      this.$root.$emit("bv::refresh::table", "my-table");
    },
    async getPriIndustriesOptions() {
      this.optionsPriIndustries = [];
      this.isLoading = true;
      let token = "Bearer " + localStorage.getItem("accessToken");
      try {
        let result = await HTTP("get", "v1/getPriIndustriesOptions", null, {
          Authorization: token
        });
        this.optionsPriIndustries = result.data.priindustriesoptions;
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
      }
    },
    setPriIndustriesValue(value) {
      this.priIndustriesValue = null;
      this.priIndustriesValue = value ? value.text : null;
    },
    openThirdpartyProfile(data) {
      this.$router.push({
        path: "/thirdparty/profile/" + data.item.id + "/" + data.item.comp_id
      });
    },
    showEditModal(data) {
      this.editThirdParty = true;
      this.thirdPartyId = data.item.id;
      this.form = {
        thirdPartyName: data.item.comp_name_eng,
        selectedEntityType: data.item.tpi_type,
        selectedCountry: data.item.country,
        website: data.item.website,
        registrationNumber: data.item.registration_number,
        selectedPriIndustries: data.item.industry
      };
      this.priIndustriesValue = data.item.industry;
      this.countryValue = data.item.country;
      this.showAddThirdPartyModal = true;
    },
    async getBenchMarkThirdPartiesData() {
      let formData = {
        filter: this.filter,
        sort: this.sortBy + "|" + (this.sortDesc == true ? "desc" : "asc"),
        per_page: this.perPage,
        page: this.currentPage,
        company_id: 0
      };
      this.isLoading = true;
      let token = "Bearer " + localStorage.getItem("accessToken");
      let result = HTTP(
        "post",
        "v1/get-benchmark-third-parties-table",
        formData,
        {
          Authorization: token
        }
      );

      return result
        .then(response => {
          const items = response.data.data;
          this.isLoading = false;
          this.fromRows = response.data.from;
          this.toRows = response.data.to;
          this.totalRows = response.data.total;
          return items;
        })
        .catch(error => {
          this.isLoading = false;
          return [error];
        });
    },
    setCountry(value) {
      this.countryValue = null;
      this.countryValue = value ? value.name : null;
    },
    checkValidation() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      } else if (this.editThirdParty) {
        this.updateThirdParty().then(this.reloadTable);
      } else {
        this.addThirdParty().then(this.reloadTable);
      }
    },
    async addThirdParty() {
      let formData = {
        thirdPartyName: this.form.thirdPartyName,
        tpiType: this.form.selectedEntityType,
        country: this.countryValue,
        website: this.form.website,
        registrationNumber: this.form.registrationNumber,
        industry: this.priIndustriesValue,
        userId: localStorage.getItem("userId")
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP("post", "v1/pri/create/benchmark/thirdparty", formData, {
          Authorization: token
        });
        this.resetForm();
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (error) {
        this.isLoading = false;
        this.submiting = false;
      }
    },
    async updateThirdParty() {
      let formData = {
        thirdPartyId: this.thirdPartyId,
        thirdPartyName: this.form.thirdPartyName,
        tpiType: this.form.selectedEntityType,
        country: this.countryValue,
        website: this.form.website,
        registrationNumber: this.form.registrationNumber,
        industry: this.priIndustriesValue,
        userId: localStorage.getItem("userId")
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP("post", "v1/pri/update/benchmark/thirdparty", formData, {
          Authorization: token
        });
        this.resetForm();
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (error) {
        this.isLoading = false;
        this.submiting = false;
      }
    },
    resetForm() {
      this.form = {
        thirdPartyName: null,
        selectedEntityType: null,
        selectedCountry: null,
        website: null,
        registrationNumber: null
      };
      this.countryValue = null;
    },
    resetModal() {
      this.resetForm();
      this.editThirdParty = false;
      this.responseSuccess = false;
      this.submiting = false;
      this.isLoading = false;
      this.showAddThirdPartyModal = false;
      this.$v.$reset();
    }
  }
};
</script>